import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { PasswordField } from './PasswordField';
import { PasswordStrengthMeter } from './PasswordStrengthMeter';
import { passwordStrength } from '../lib/passwordStrength';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Plane, Mail, User } from 'lucide-react';

// Global sign-in popup. Opened via useAuth().openAuthModal(action) / requireAuth(action).
// After email/password auth, the pending action runs (e.g. completing a save). Google redirects.
export const AuthDialog = () => {
  const { authModalOpen, closeAuthModal, login, signup } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [signupForm, setSignupForm] = useState({ name: '', email: '', password: '' });

  const startGoogle = () => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    window.location.href = `${backend}/api/auth/google/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`;
  };

  const doLogin = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await login(loginForm); toast.success('Welcome back!'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Login failed'); }
    finally { setLoading(false); }
  };
  const doSignup = async (e) => {
    e.preventDefault();
    if (!passwordStrength(signupForm.password).valid) {
      toast.error('Password must be at least 8 characters and include a letter and a number.');
      return;
    }
    setLoading(true);
    try { await signup(signupForm); toast.success('Account created!'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Signup failed'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={authModalOpen} onOpenChange={(o) => { if (!o) closeAuthModal(); }}>
      <DialogContent className="sm:max-w-md" data-testid="auth-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <span className="inline-grid place-items-center h-9 w-9 rounded-xl bg-accent text-accent-foreground"><Plane className="h-4 w-4" /></span>
            Sign in to continue
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">Save flights, hotels & experiences, track prices and plan trips.</p>

        <Button onClick={startGoogle} variant="outline" className="w-full h-11 rounded-xl" data-testid="auth-dialog-google">
          <img src="https://www.google.com/favicon.ico" alt="" className="h-4 w-4 mr-2" /> Continue with Google
        </Button>
        <div className="flex items-center gap-3"><div className="h-px bg-border flex-1" /><span className="text-xs text-muted-foreground">or</span><div className="h-px bg-border flex-1" /></div>

        <Tabs defaultValue="login">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="login" data-testid="auth-dialog-tab-login">Sign in</TabsTrigger>
            <TabsTrigger value="signup" data-testid="auth-dialog-tab-signup">Create account</TabsTrigger>
          </TabsList>
          <TabsContent value="login">
            <form onSubmit={doLogin} className="space-y-3 mt-3">
              <div><Label>Email</Label><div className="relative"><Mail className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" /><Input type="email" required className="pl-9 h-11" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} data-testid="auth-dialog-login-email" /></div></div>
              <div><Label>Password</Label><PasswordField value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} autoComplete="current-password" required testid="auth-dialog-login-password" /></div>
              <Button type="submit" disabled={loading} className="w-full h-11 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" data-testid="auth-dialog-login-submit">Sign in</Button>
            </form>
          </TabsContent>
          <TabsContent value="signup">
            <form onSubmit={doSignup} className="space-y-3 mt-3">
              <div><Label>Name</Label><div className="relative"><User className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" /><Input required className="pl-9 h-11" value={signupForm.name} onChange={(e) => setSignupForm({ ...signupForm, name: e.target.value })} data-testid="auth-dialog-signup-name" /></div></div>
              <div><Label>Email</Label><div className="relative"><Mail className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" /><Input type="email" required className="pl-9 h-11" value={signupForm.email} onChange={(e) => setSignupForm({ ...signupForm, email: e.target.value })} data-testid="auth-dialog-signup-email" /></div></div>
              <div><Label>Password</Label><PasswordField value={signupForm.password} onChange={(e) => setSignupForm({ ...signupForm, password: e.target.value })} autoComplete="new-password" required minLength={8} testid="auth-dialog-signup-password" /><PasswordStrengthMeter value={signupForm.password} testid="auth-dialog-strength" /></div>
              <Button type="submit" disabled={loading || !passwordStrength(signupForm.password).valid} className="w-full h-11 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" data-testid="auth-dialog-signup-submit">Create account</Button>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
